// CumulativeInterest.jsx
import React, {useEffect, useState, useRef, useCallback} from 'react';
import styles from './TransactionHistory.module.css';
import {shortenAddress, formatAmount, useIsMobile} from '../utils';
import {USDI_API_URL} from "../../configs/config";

import arrowDownSvg from '../static_files/tx-arrow-down.svg';
import uIconSvg from '../static_files/usdi.svg';
import arrowRightSvg from '../static_files/tx-right-arrow.svg';
import expandSvg from '../static_files/tx-expand.svg';

import shareImg from '../static_files/share-selector.svg';
import shareHoverImg from '../static_files/share-selector-hover.svg';
import TooltipLeft from "../Tooltip/TooltipLeft";

const USDI_API = 'https://usdi-api-256611876551.europe-west1.run.app';
const getUserBalanceEndpoint =
    'https://distribution-calculator-256611876551.europe-west1.run.app/get_user_balance_solana';
// Use Helius RPC for better historical data access (same as used in other parts of the codebase)
const SOLANA_RPC = 'https://mainnet.helius-rpc.com/?api-key=87eb0292-8ed2-4c97-94a3-2a325517875b';
// Fallback RPC endpoints
const ANKR_RPC = 'https://rpc.ankr.com/solana/80465c767c6a5751c4cadf0778b6917d26c2d0a9ae583d3616ffaae71191a7c4';
const QUICKNODE_RPC = 'https://solitary-boldest-thunder.solana-mainnet.quiknode.pro/7e14722814e9fc29ad81532c2da66c2ffbe3781f';

const safeParse = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

// 1）查询链上区块时间
async function fetchTxTime(signature) {
    try {
        // Try with finalized commitment level first
        const body = {
            jsonrpc: '2.0',
            id: 1,
            method: 'getTransaction',
            params: [
                signature, 
                {
                    encoding: 'json', 
                    maxSupportedTransactionVersion: 0,
                    commitment: 'finalized' // Add commitment level
                }
            ],
        };
        console.log(`Fetching tx time for signature: ${signature}`);
        const res = await fetch(SOLANA_RPC, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(body),
        });
        
        if (!res.ok) {
            console.warn(`RPC request failed with status ${res.status} for tx ${signature}`);
            return null;
        }
        
        const json = await res.json();
        
        // Check for RPC errors
        if (json.error) {
            console.warn(`RPC error for tx ${signature}:`, json.error);
            return null;
        }
        
        // Check if transaction result exists
        if (!json.result) {
            console.warn(`Transaction not found on-chain: ${signature}`);
            console.log('Full RPC response:', JSON.stringify(json, null, 2));
            console.log(`You can verify this transaction at: https://solscan.io/tx/${signature}`);
            // Try with maxSupportedTransactionVersion: null to support all versions
            return await fetchTxTimeWithFallback(signature);
        }
        
        const ts = json?.result?.blockTime;
        if (!ts) {
            console.warn(`No blockTime in transaction result for ${signature}. Transaction may not be confirmed.`);
            return null;
        }
        
        const date = new Date(ts * 1000);
        console.log(`Successfully fetched tx time for ${signature}:`, date);
        return date; // 这里返回的是一个 JS Date 对象（本地时区）
    } catch (error) {
        console.error(`Error fetching tx time for ${signature}:`, error);
        return null;
    }
}

// Fallback method: try without maxSupportedTransactionVersion constraint, then try Solscan API
async function fetchTxTimeWithFallback(signature) {
    try {
        // First, try RPC without maxSupportedTransactionVersion
        const body = {
            jsonrpc: '2.0',
            id: 1,
            method: 'getTransaction',
            params: [signature, {encoding: 'json'}], // Remove maxSupportedTransactionVersion
        };
        console.log(`Trying fallback RPC method for signature: ${signature}`);
        const res = await fetch(SOLANA_RPC, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(body),
        });
        
        if (res.ok) {
            const json = await res.json();
            
            if (!json.error && json.result && json.result.blockTime) {
                const date = new Date(json.result.blockTime * 1000);
                console.log(`Fallback RPC succeeded for ${signature}:`, date);
                return date;
            }
        }
        
        // If RPC fails, try Solscan API as last resort
        console.log(`RPC failed, trying Solscan API for ${signature}`);
        return await fetchTxTimeFromSolscan(signature);
    } catch (error) {
        console.error(`Fallback error for ${signature}:`, error);
        // Try Solscan as last resort
        return await fetchTxTimeFromSolscan(signature);
    }
}

// Fallback: Try alternative RPC endpoints
async function fetchTxTimeFromSolscan(signature) {
    try {
        console.log(`Trying alternative RPC endpoints for ${signature}`);
        
        // Try different RPC endpoints
        const rpcEndpoints = [
            { name: 'Helius', url: SOLANA_RPC },
            { name: 'QuickNode', url: QUICKNODE_RPC },
            { name: 'Ankr', url: ANKR_RPC },
        ];
        
        // Try different commitment levels
        const commitments = ['finalized', 'confirmed', 'processed'];
        
        for (const rpc of rpcEndpoints) {
            for (const commitment of commitments) {
                try {
                    const body = {
                        jsonrpc: '2.0',
                        id: 1,
                        method: 'getTransaction',
                        params: [
                            signature,
                            {
                                encoding: 'json',
                                commitment: commitment
                            }
                        ],
                    };
                    
                    console.log(`Trying ${rpc.name} RPC with ${commitment} commitment`);
                    const res = await fetch(rpc.url, {
                        method: 'POST',
                        headers: {'Content-Type': 'application/json'},
                        body: JSON.stringify(body),
                    });
                    
                    if (res.ok) {
                        const json = await res.json();
                        if (json.result && json.result.blockTime) {
                            const date = new Date(json.result.blockTime * 1000);
                            console.log(`Success! ${rpc.name} RPC (${commitment}) found transaction:`, date);
                            return date;
                        }
                    }
                } catch (err) {
                    // Continue to next RPC/commitment combination
                    continue;
                }
            }
        }
        
        console.warn(`All RPC endpoints and methods failed for ${signature}. Transaction may be archived or RPCs don't have access.`);
        return null;
    } catch (error) {
        console.error(`Alternative RPC endpoints error for ${signature}:`, error);
        return null;
    }
}

async function updateDistributionTime(rowId, dateObj) {
    try {
        const isoUtc = dateObj.toISOString();

        const updateUrl = `${USDI_API_URL}/update?table=user_balance_blocks_solana&key=id&value=${rowId}`;
        await fetch(updateUrl, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                distribution_time: isoUtc,
            }),
        });
    } catch (e) {
        console.error('Failed to update distribution_time:', e);
    }
}

function CumulativeInterest({onHasCumulativeInterest,onDataLoaded,  walletAddress: addrProp}) {

    const isSmallScreen = useIsMobile(1000);
    const walletAddress =
        addrProp || localStorage.getItem('walletAddress') || localStorage.getItem('emailUserWalletAddress') || '';

    const [interestData, setInterestData] = useState([]);
        const [isCumLoading, setIsCumLoading] = useState(false);

    const [tooltipTextMap, setTooltipTextMap] = useState({});
    const [accruedInterest, setAccruedInterest] = useState(0);
    const scrollRef = useRef(null);
    const [scrollInfo, setScrollInfo] = useState({
        scrollTop: 0,
        scrollHeight: 0,
        clientHeight: 0,
    });
    const updateScrollInfo = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        setScrollInfo({
            scrollTop: el.scrollTop,
            scrollHeight: el.scrollHeight,
            clientHeight: el.clientHeight,
        });
    }, []);

    // —— 通知父组件是否存在Cumulative Interest数据 ——
    useEffect(() => {
        onHasCumulativeInterest?.(interestData.length > 0);
    }, [interestData]);
    useEffect(() => {
        onDataLoaded?.({isCumLoading});
    }, [isCumLoading]);
    useEffect(() => {
        if (!walletAddress) return;

        const fetchData = async () => {
            try {
                const newData = [];
                // 1. Accrued‑Interest row
                const holdersUrl = `${USDI_API}/usdi_holders_info_table_solana?address=${walletAddress}`;
                const holdersRes = await fetch(holdersUrl);
                if (!holdersRes.ok) throw new Error('Failed to fetch holders‑info');
                const holdersJson = await holdersRes.json();
                const hRow = holdersJson?.[0];

                if (hRow) {
                    newData.push({
                        id: 'accrued',
                        label: 'Accrued Interest',
                        arrow: 'right',
                        amount: safeParse(hRow.accrued_interest),
                        cumInterest: safeParse(hRow.cum_distributed_usdi),
                        time: 'Accruing',
                        to: shortenAddress(walletAddress),
                        signature: null,
                    });
                    setAccruedInterest(safeParse(hRow.accrued_interest))
                }

                // 2. Interest‑Deposit rows (from getUserBalanceEndpoint)
                const balUrl = `${getUserBalanceEndpoint}?user_address=${walletAddress}`;
                const balRes = await fetch(balUrl);

                const balJson = await balRes.json();
                if (!balRes.ok && balJson.message !=='No data found for the specified user_address.')
                {
                    console.log('Failed to fetch balance detail');
                }


                const rows = (balJson?.data || [])
                    .filter(
                        (r) =>
                            r.distribution_transaction_hash &&
                            r.distribution_transaction_hash !== '0' &&
                            r.distribution_transaction_hash.toLowerCase() !== 'none'
                    )
                    .sort((a, b) => Number(b.block_number) - Number(a.block_number));

                // 逐行处理
                const depositRows = await Promise.all(
                    rows.map(async (r, idx) => {
                        const undistributed = safeParse(r.undistributed_usdi);
                        const cumDist = safeParse(r.cum_distributed_usdi);
                        // ★ step1: 看看后端是否已经给了 distribution_time
                        let dt = null;
                        if (
                            r.distribution_time &&
                            r.distribution_time !== '' &&
                            r.distribution_time.toLowerCase() !== 'none'
                        ) {
                            dt = new Date(r.distribution_time);
                        }
                        // ★ step2: 如果 distribution_time 还没有，就走链上查询
                        if (!dt && r.distribution_transaction_hash) {
                            dt = await fetchTxTime(r.distribution_transaction_hash);
                            // 拿到链上返回的 Date（本地时区）
                            // —先显示—
                            // —然后再异步调用 update 分发时间，存入数据库 (UTC)—
                            if (dt && r.id) {
                                // 不阻塞 UI，直接异步调
                                updateDistributionTime(r.id, dt);
                            }
                        }

                        // ★ step3: 用本地时间格式在页面显示
                        let timeStr = '-';
                        if (dt) {
                            timeStr = dt.toLocaleString('en-US', {
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true,    // << Turn on 12-hour format
                            });
                        }
                        return {
                            id: r.id || `deposit_${idx}`, // 有 id 就用 r.id，否则用 deposit_xxx
                            label: 'Interest Deposit',
                            arrow: 'left',
                            amount: undistributed,
                            cumInterest: undistributed + cumDist,
                            time: timeStr,
                            to: shortenAddress(walletAddress),
                            signature: r.distribution_transaction_hash,
                        };
                    })
                );

                newData.push(...depositRows);
                setInterestData(newData);
                setIsCumLoading(true);
            } catch (e) {
                console.error(e);
            }
        };

        fetchData();
    }, [walletAddress]);

    useEffect(() => {
        if (interestData.length > 5) {
            updateScrollInfo();
            const id = requestAnimationFrame(() => updateScrollInfo());
            return () => cancelAnimationFrame(id);
        }
    }, [interestData.length, interestData, updateScrollInfo]);

    // 点击跳转到链上浏览器
    const handleRedirectClick = (tx) => {
        if (!tx.signature) return;
        window.open(`https://solscan.io/tx/${tx.signature}`, '_blank');
        setTooltipTextMap((prev) => ({...prev, [tx.id]: 'Link Copied'}));
        setTimeout(() => {
            setTooltipTextMap((prev) => {
                const copy = {...prev};
                delete copy[tx.id];
                return copy;
            });
        }, 1000);
    };

    if (!interestData.length) return null;

    const showScroll = interestData.length > 5;
    const containerHeight = !isSmallScreen ? '375px' : '500px';
    /** Pixels between row block right edge and scrollbar (bar sits outside row width) */
    const scrollbarOffsetFromRows = 8;

    return (
        <div style={{marginBottom: 20, position: 'relative'}}>
            <div className={styles.transactionsDayTitle} style={{marginBottom: 10}}>
                Cumulative Interest
            </div>

            <div
                style={{
                    position: 'relative',
                    maxWidth: 1176,
                    width: '100%',
                    overflow: 'visible',
                }}
            >
                <div
                    ref={scrollRef}
                    className={`${styles.requestContainer} ${
                        showScroll
                            ? `${styles.scrollableContainer} ${styles.scrollableContainerPersistent}`
                            : ''
                    }`}
                    style={{
                        width: '100%',
                        maxHeight: containerHeight,
                        overflowY: showScroll ? 'scroll' : 'visible',
                    }}
                    onScroll={showScroll ? updateScrollInfo : undefined}
                >
                    {interestData.map((tx) => {
                    let arrowIcon;
                    if (tx.arrow === 'down') {
                        arrowIcon = arrowDownSvg;
                    } else if (tx.arrow === 'left') {
                        arrowIcon = arrowRightSvg; // Use right arrow and flip it with CSS
                    } else {
                        arrowIcon = arrowRightSvg;
                    }

                    return (
                        <div
                            key={tx.id}
                            className={styles.transactionRowOuterContainer}
                            style={{position: 'relative', overflow: 'visible'}}
                        >
                            <div className={styles.transactionRow} style={{overflow: 'visible'}}>
                                <div className={`${styles.txTypeGroup} ${styles.txTypeGroupRequested}`}
                                     style={{marginLeft: "0"}}>
                                    <img 
                                        className={`${styles.directionArrow} ${tx.arrow === 'left' ? styles.directionArrowGreen : ''}`}
                                        src={arrowIcon} 
                                        alt=""
                                        style={tx.arrow === 'left' ? {transform: 'scaleX(-1)'} : {}}
                                    />
                                    <span className={styles.txType}>{tx.label}</span>
                                </div>

                                <div className={styles.uIconGroup}>
                                    <img className={styles.uIcon} src={uIconSvg} alt="USDi"/>
                                    <div className={styles.amountBox}>
                                        <span className={styles.amount}>{formatAmount(tx.amount, 6)}</span>
                                        <span className={styles.usdi}>USDi</span>
                                    </div>
                                </div>

                                <div className={styles.toAddressGroup}>
                                    <span className={styles.to}>To:</span>
                                    <span className={styles.toAddress}>{tx.to}</span>
                                </div>

                                <span className={`${styles.time} ${styles.time2}`}>{tx.time}</span>
                                {!isSmallScreen && (
                                    <span
                                        className={`${styles.statusCompleted} ${styles.cumInterest}`}
                                        style={{display: 'flex', flexDirection: 'column'}}
                                    >
                                  <div>
                                       {tx.label === "Accrued Interest" ?
                                           <span style={{fontSize: 16}}>
                                      {(Number(tx.cumInterest) + accruedInterest).toFixed(6)}
                                    </span> : <span style={{fontSize: 16}}>
                                      {(Number(tx.cumInterest)).toFixed(6)}
                                    </span>
                                       }
                                      <span className={styles.greyStatusText} style={{marginLeft: 5, fontSize: 10}}>
                                      USDi
                                    </span>
                                  </div>
                                  <span className={styles.greyStatusText} style={{marginTop: 0}}>
                                    Cum Interest
                                  </span>
                                </span>)}

                                <div className={styles.shareIconGroup}>
                                    {isSmallScreen && (
                                        <span
                                            className={`${styles.statusCompleted} ${styles.cumInterest}`}
                                            style={{display: 'flex', flexDirection: 'column'}}
                                        >
                                          <div>
                                               {tx.label === "Accrued Interest" ?
                                                   <span style={{fontSize: 16}}>
                                              {(Number(tx.cumInterest) + accruedInterest).toFixed(6)}
                                            </span> : <span style={{fontSize: 16}}>
                                              {(Number(tx.cumInterest)).toFixed(6)}
                                            </span>
                                               }
                                              <span className={styles.greyStatusText}
                                                    style={{marginLeft: 5, fontSize: 10}}>
                                              USDi
                                            </span>
                                          </div>
                                          <span className={styles.greyStatusText} style={{marginTop: 0}}>
                                            Cum Interest
                                          </span>
                                        </span>

                                    )}
                                    <div className={styles.shareBtn} style={{display: "flex"}}>
                                        {tx.label === "Accrued Interest" ? <img
                                                src={tooltipTextMap[tx.id] === 'Link Copied' ? shareHoverImg : shareImg}
                                                alt="share"
                                                width={16}
                                                height={16}
                                                style={{visibility: 'hidden'}}
                                            /> :
                                            <TooltipLeft text="Transaction Details">
                                                <img
                                                    src={tooltipTextMap[tx.id] === 'Link Copied' ? shareHoverImg : shareImg}
                                                    alt="share"
                                                    width={16}
                                                    height={16}
                                                    onMouseEnter={(e) => {
                                                        if (tooltipTextMap[tx.id] !== 'Link Copied') e.currentTarget.src = shareHoverImg;
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        if (tooltipTextMap[tx.id] !== 'Link Copied') e.currentTarget.src = shareImg;
                                                    }}
                                                    onClick={() => handleRedirectClick(tx)}
                                                    style={{cursor: 'pointer'}}
                                                /></TooltipLeft>}


                                    </div>

                                    {isSmallScreen ? null : (
                                        <img
                                            className={styles.expandIcon}
                                            src={expandSvg}
                                            alt="expand"
                                            style={{visibility: 'hidden'}}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
                </div>
                {showScroll &&
                    (() => {
                        const {scrollTop, scrollHeight, clientHeight} = scrollInfo;
                        const trackHeight = clientHeight || (isSmallScreen ? 500 : 375);
                        const maxScroll = Math.max(0, scrollHeight - clientHeight);
                        const thumbRatio =
                            clientHeight && scrollHeight ? clientHeight / scrollHeight : 1;
                        const thumbHeight = Math.max(20, Math.round(trackHeight * thumbRatio));
                        const thumbTop =
                            maxScroll > 0
                                ? (scrollTop / maxScroll) * (trackHeight - thumbHeight)
                                : 0;
                        return (
                            <div
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: '100%',
                                    marginLeft: scrollbarOffsetFromRows,
                                    width: 6,
                                    height: containerHeight,
                                    maxHeight: containerHeight,
                                    background: 'rgba(232, 234, 237, 0.85)',
                                    borderRadius: 3,
                                    pointerEvents: 'none',
                                }}
                                aria-hidden="true"
                            >
                                <div
                                    style={{
                                        position: 'absolute',
                                        left: 0,
                                        top: thumbTop,
                                        width: 6,
                                        height: thumbHeight,
                                        background: 'rgba(180, 186, 198, 0.65)',
                                        borderRadius: 3,
                                    }}
                                />
                            </div>
                        );
                    })()}
            </div>
        </div>
    );
}

export default CumulativeInterest;
